import React from 'react';
import { ArrowLeft, FileText, Shield, AlertTriangle, Users, Gavel, CreditCard, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/40 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/auth" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              BillPortal
            </span>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Title */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto">
            <Gavel className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-muted-foreground">
            Effective Date: January 26, 2025 | Version 1.0
          </p>
        </div>

        {/* Introduction */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Agreement to Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              These Terms of Service ("Terms") govern your use of BillPortal, an enterprise bill processing 
              and document management platform operated by BillPortal Inc. ("we," "us," or "our").
            </p>
            <p>
              By accessing or using BillPortal, you agree to be bound by these Terms and our Privacy Policy. 
              If you do not agree to these Terms, you may not access or use our services.
            </p>
            <div className="bg-warning/10 p-4 rounded-lg border border-warning/20">
              <p className="text-warning text-sm font-medium">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                BillPortal is an invitation-only platform. Access is restricted to authorized users only.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Service Description */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Service Description
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              BillPortal provides secure, AI-powered document processing services including:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold">Core Services</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Bill and invoice processing</li>
                  <li>• AI-powered data extraction</li>
                  <li>• Workflow automation</li>
                  <li>• Document management</li>
                  <li>• Analytics and reporting</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Enterprise Features</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Multi-user workspaces</li>
                  <li>• Role-based access control</li>
                  <li>• API integrations</li>
                  <li>• Advanced security features</li>
                  <li>• Custom processing rules</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Responsibilities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              User Responsibilities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Account Security</h4>
                <p className="text-muted-foreground text-sm">
                  You are responsible for maintaining the confidentiality of your account credentials 
                  and for all activities that occur under your account. Use strong passwords and 
                  enable multi-factor authentication when available.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Lawful Use</h4>
                <p className="text-muted-foreground text-sm">
                  You may only use BillPortal for lawful purposes and in accordance with these Terms. 
                  You may not use our services to process illegal content or engage in fraudulent activities.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Data Accuracy</h4>
                <p className="text-muted-foreground text-sm">
                  You are responsible for the accuracy and legality of data you upload to BillPortal. 
                  Ensure you have proper authorization before uploading third-party documents.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Workspace Management</h4>
                <p className="text-muted-foreground text-sm">
                  Workspace administrators are responsible for managing user access, permissions, 
                  and ensuring compliance with their organization's policies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prohibited Uses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Prohibited Uses
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>You may not use BillPortal to:</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-destructive">Security Violations</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Attempt unauthorized access</li>
                  <li>• Interfere with service security</li>
                  <li>• Reverse engineer our systems</li>
                  <li>• Share account credentials</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold text-destructive">Illegal Activities</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Process fraudulent documents</li>
                  <li>• Violate privacy laws</li>
                  <li>• Infringe intellectual property</li>
                  <li>• Engage in money laundering</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold text-destructive">Service Abuse</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Overload our systems</li>
                  <li>• Create excessive accounts</li>
                  <li>• Resell our services</li>
                  <li>• Use automated scraping</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold text-destructive">Content Violations</h4>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Upload malicious content</li>
                  <li>• Share inappropriate material</li>
                  <li>• Violate third-party rights</li>
                  <li>• Process personal data illegally</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Intellectual Property Rights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Our Rights</h4>
                <p className="text-muted-foreground text-sm">
                  BillPortal and its technology, including software, algorithms, and user interfaces, 
                  are protected by intellectual property laws. You may not copy, modify, or 
                  distribute our proprietary technology.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Your Content</h4>
                <p className="text-muted-foreground text-sm">
                  You retain ownership of documents and data you upload to BillPortal. By using 
                  our service, you grant us a limited license to process your content to provide 
                  our services.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Feedback</h4>
                <p className="text-muted-foreground text-sm">
                  Any feedback, suggestions, or ideas you provide about BillPortal become our 
                  property and may be used to improve our services without compensation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Service Availability & Disclaimers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Service Availability</h4>
                <p className="text-muted-foreground text-sm">
                  We strive to maintain 99.9% uptime but cannot guarantee uninterrupted service. 
                  Scheduled maintenance will be announced in advance when possible.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">AI Processing Disclaimer</h4>
                <p className="text-muted-foreground text-sm">
                  Our AI-powered extraction is designed for high accuracy but may contain errors. 
                  Always review extracted data before making business decisions. We are not liable 
                  for decisions based on AI-processed data.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Data Backup</h4>
                <p className="text-muted-foreground text-sm">
                  While we maintain regular backups, you are responsible for maintaining your own 
                  copies of important documents and data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limitation of Liability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Limitation of Liability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-warning/10 p-4 rounded-lg border border-warning/20">
              <p className="text-warning text-sm font-medium mb-2">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                Important Legal Disclaimer
              </p>
              <p className="text-warning text-sm">
                BillPortal is provided "as is" without warranties of any kind. We disclaim all 
                warranties, express or implied, including merchantability and fitness for a particular purpose.
              </p>
            </div>
            
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Limitation of Damages</h4>
                <p className="text-muted-foreground text-sm">
                  In no event shall BillPortal be liable for any indirect, incidental, special, 
                  consequential, or punitive damages, including loss of profits, data, or business 
                  opportunities, even if we have been advised of the possibility of such damages.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Maximum Liability</h4>
                <p className="text-muted-foreground text-sm">
                  Our total liability to you for all claims arising from your use of BillPortal 
                  shall not exceed the amount you paid for the service in the 12 months preceding 
                  the claim.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Termination */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Account Termination
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Termination by You</h4>
                <p className="text-muted-foreground text-sm">
                  You may terminate your account at any time through your account settings. 
                  Upon termination, your access will cease immediately, and your data will be 
                  deleted according to our Privacy Policy.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Termination by Us</h4>
                <p className="text-muted-foreground text-sm">
                  We may suspend or terminate your account immediately if you violate these Terms, 
                  engage in fraudulent activity, or for any other reason at our sole discretion.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Effect of Termination</h4>
                <p className="text-muted-foreground text-sm">
                  Upon termination, your license to use BillPortal terminates, and you must cease 
                  all use of our services. Provisions regarding intellectual property, liability, 
                  and dispute resolution survive termination.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-primary" />
              Governing Law & Disputes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Governing Law</h4>
                <p className="text-muted-foreground text-sm">
                  These Terms are governed by the laws of [Your Jurisdiction], without regard to 
                  conflict of law principles.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Dispute Resolution</h4>
                <p className="text-muted-foreground text-sm">
                  Any disputes arising from these Terms or your use of BillPortal will be resolved 
                  through binding arbitration in accordance with the rules of [Arbitration Organization].
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold">Class Action Waiver</h4>
                <p className="text-muted-foreground text-sm">
                  You agree that any arbitration or legal proceeding will be conducted on an 
                  individual basis and not as part of a class action or collective proceeding.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Changes to Terms
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              We may update these Terms from time to time to reflect changes in our services, 
              legal requirements, or business practices. We will notify you of material changes 
              via email or platform notification at least 30 days before they take effect.
            </p>
            <p>
              Your continued use of BillPortal after the effective date of any changes constitutes 
              your acceptance of the updated Terms.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              If you have questions about these Terms of Service, please contact us:
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Email: <strong>legal@billportal.ai</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Support: <strong>support@billportal.ai</strong></span>
              </div>
            </div>
            
            <Separator />
            
            <p className="text-xs text-muted-foreground">
              Last updated: January 26, 2025. These Terms of Service are effective immediately 
              upon posting and supersede all previous versions.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}